<template>
  <div class="gs-video-preview">
    <template v-if="parsed">
      <div class="gs-video-preview__frame">
        <img
          v-if="thumbnail"
          class="gs-video-preview__thumb"
          :src="thumbnail"
          :alt="`${providerLabel} video thumbnail`"
        />
        <span v-else class="gs-video-preview__placeholder" aria-hidden="true">
          <gs-icon name="play" size="24"></gs-icon>
        </span>
      </div>
      <div class="gs-video-preview__meta">
        <gs-badge>{{ providerLabel }}</gs-badge>
        <span class="gs-video-preview__id">{{ parsed.id }}</span>
      </div>
    </template>

    <p v-else class="gs-video-preview__note">Paste a YouTube or Vimeo link</p>
  </div>
</template>

<script>
import urlParser from 'js-video-url-parser/lib/base';
// The parser only knows the providers that are imported alongside it, so the
// two this screen supports are pulled in here as well as in `assets.vue`.
import 'js-video-url-parser/lib/provider/youtube';
import 'js-video-url-parser/lib/provider/vimeo';

const PROVIDER_LABELS = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
};

export default {
  name: 'VideoPreview',

  props: {
    url: {
      type: String,
      default: '',
    },
  },

  computed: {
    parsed() {
      if (!this.url) return null;

      // `parse` throws for some malformed input rather than returning
      // undefined, and a bad paste must never take the editor down.
      let result;
      try {
        result = urlParser.parse(this.url);
      } catch (error) {
        return null;
      }

      return result && result.id && PROVIDER_LABELS[result.provider] ? result : null;
    },

    providerLabel() {
      return this.parsed ? PROVIDER_LABELS[this.parsed.provider] : '';
    },

    // Vimeo thumbnails need an API call, so only YouTube gets a real image;
    // Vimeo falls back to the badge plus the id.
    thumbnail() {
      if (!this.parsed || this.parsed.provider !== 'youtube') return '';
      return `https://img.youtube.com/vi/${this.parsed.id}/hqdefault.jpg`;
    },
  },
};
</script>

<style scoped>
.gs-video-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.gs-video-preview__frame {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 160px;
  height: 90px;
  overflow: hidden;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-video-preview__thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gs-video-preview__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.gs-video-preview__id {
  min-width: 0;
  overflow: hidden;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-video-preview__note {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}
</style>
